import { ChangeEvent, useState } from 'react';
import { useFormContext, useFieldArray, useWatch } from 'react-hook-form';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { RHFTextField, RHFSwitch } from '../../../components/hook-form';
import Iconify from '../../../components/Iconify';
import {
  parseQuestionsFile,
  downloadQuestionsTemplate,
} from '../course/course-section-forms/Quiz/importQuestions';

// Answer options for a single question. `correct` marks the right option(s);
// the backend re-grades server-side against these, so the marking is trusted.
function AnswersField({ base }: { base: string }) {
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({ control, name: `${base}.answers` });

  return (
    <Stack spacing={1} sx={{ pl: 2, mt: 1 }}>
      {fields.map((f, i) => (
        <Stack key={f.id} direction="row" spacing={1} alignItems="center">
          <RHFTextField name={`${base}.answers.${i}.name`} label={`Option ${i + 1}`} size="small" fullWidth />
          {/* Arabic twin. Without it the schema field exists but is unfillable,
              so every hand-authored option was saved with an empty name_ar and
              Arabic learners saw English. */}
          <RHFTextField
            name={`${base}.answers.${i}.name_ar`}
            label={`الخيار ${i + 1}`}
            size="small"
            fullWidth
            inputProps={{ dir: 'rtl' }}
          />
          <RHFSwitch name={`${base}.answers.${i}.correct`} label="Correct" />
          <IconButton color="error" size="small" onClick={() => remove(i)}>
            <Iconify icon="eva:trash-2-outline" />
          </IconButton>
        </Stack>
      ))}
      <Button
        size="small"
        onClick={() => append({ name: '', name_ar: '', correct: false, points: 1, minusPoints: 0 })}
        startIcon={<Iconify icon="eva:plus-fill" />}
        sx={{ alignSelf: 'flex-start' }}
      >
        Add option
      </Button>
    </Stack>
  );
}

// Optional per-lecture quiz builder. Mirrors the course quiz item: quiz-level
// settings (pass %, attempts, time) + a list of MCQ questions with answer
// options. Leaving the questions list empty means "no quiz on this lecture".
export default function LectureQuizBuilder({ lectureIndex }: { lectureIndex: number }) {
  const { control, setValue } = useFormContext();
  const base = `lectures.${lectureIndex}.quiz`;
  const { fields, append, remove } = useFieldArray({ control, name: `${base}.questions` });

  // Collapsed by default — a program with several lectures otherwise renders an
  // enormous form. The header still shows the question count and the on/off
  // state, so nothing is hidden informationally.
  const [open, setOpen] = useState(false);

  // `active !== false` so a legacy quiz saved before this flag existed (no
  // `active` key at all) still reads as enabled.
  const activeValue = useWatch({ control, name: `${base}.active` });
  const isActive = activeValue !== false;

  // Only batches that already exist server-side can be excluded — a batch added
  // in an unsaved form has no _id, so an exclusion against it would be a no-op.
  const allBatches = useWatch({ control, name: 'batches' }) as any[] | undefined;
  const savedBatches = (allBatches || []).filter((b: any) => b?._id);
  const excludedIds = ((useWatch({ control, name: `${base}.excludedBatches` }) as any[]) || []).map(
    (id: any) => String(id),
  );

  // Bulk-import questions from the unified CSV/JSON template. Parsed questions use
  // the canonical MCQ shape (title/title_ar/multi/answers[{name,name_ar,points,
  // correct}]) — identical to a course quiz — so they append straight in.
  const handleImport = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const parsed = await parseQuestionsFile(file);
      parsed.forEach((q: any) => {
        append({
          title: q.title || '',
          title_ar: q.title_ar || '',
          description: q.description || '',
          multi: !!q.multi,
          type: 'mcq',
          answers: (q.answers || []).map((a: any) => ({
            name: a.name || '',
            name_ar: a.name_ar || '',
            points: Number(a.points ?? (a.correct ? 1 : 0)) || 0,
            minusPoints: Number(a.minusPoints ?? 0) || 0,
            correct: !!a.correct,
          })),
        });
      });
    } catch {
      // Bad file — ignore silently; the instructor can re-pick a valid template.
    }
  };

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 1,
        // Dim the whole block when the quiz is switched off.
        opacity: isActive ? 1 : 0.6,
      }}
    >
      {/* Header: click anywhere to expand/collapse. */}
      <Stack
        direction="row"
        alignItems="center"
        sx={{ flexWrap: 'wrap', gap: 1, cursor: 'pointer' }}
        onClick={() => setOpen((o) => !o)}
      >
        <Iconify
          icon={open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
          sx={{ color: 'text.secondary' }}
        />
        <Typography variant="subtitle2">Quiz (optional)</Typography>
        <Chip size="small" label={`${fields.length} question${fields.length === 1 ? '' : 's'}`} />
        {!isActive && <Chip size="small" color="default" label="Disabled" />}

        <Box sx={{ flexGrow: 1 }} />

        {/* These must not toggle the collapse. */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          onClick={(e) => e.stopPropagation()}
        >
          <RHFSwitch name={`${base}.active`} label="Active" />
          <RHFSwitch name={`${base}.showAnswers`} label="Show answers" />
          <Button size="small" onClick={() => downloadQuestionsTemplate()} startIcon={<Iconify icon="eva:download-outline" />}>
            Template
          </Button>
          <Button size="small" component="label" startIcon={<Iconify icon="eva:upload-outline" />}>
            Import CSV
            <input hidden type="file" accept=".csv,.json,.txt" onChange={handleImport} />
          </Button>
        </Stack>
      </Stack>

      {/* No unmountOnExit: keeping the inputs mounted preserves react-hook-form
          registration, so collapsing can never drop entered values. */}
      <Collapse in={open}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, mb: 1 }}>
        Turning the quiz off hides it from learners and rejects new submissions,
        without deleting its questions or past results.
        <br />
        <strong>Show answers</strong> publishes the answer key, which <strong>closes the quiz</strong>:
        nobody can start or retake it while this is on. Students who already attempted it see their
        result plus the correct answers; students who never took it are told it is closed. Turn it on
        once the cohort has finished — with it off, learners see their score and nothing else.
        Turning it back off reopens the quiz, but not for anyone who already viewed the answers —
        they are locked out of further attempts for good.
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <RHFTextField name={`${base}.title`} label="Quiz title" size="small" sx={{ minWidth: 200, flex: 1 }} />
        <RHFTextField
          name={`${base}.title_ar`}
          label="عنوان الاختبار"
          size="small"
          sx={{ minWidth: 200, flex: 1 }}
          inputProps={{ dir: 'rtl' }}
        />
        <RHFTextField name={`${base}.passingScore`} label="Pass %" type="number" size="small" sx={{ width: 110 }} />
        <RHFTextField name={`${base}.attemptsLimit`} label="Attempts" type="number" size="small" sx={{ width: 110 }} />
        <RHFTextField name={`${base}.duration`} label="Time (min)" type="number" size="small" sx={{ width: 120 }} />
      </Stack>

      {/* Per-cohort carve-out. Only SAVED batches can be excluded: a batch added
          in an unsaved form has no _id yet, so selecting it would silently do
          nothing. */}
      <Autocomplete
        multiple
        size="small"
        options={savedBatches}
        value={savedBatches.filter((b: any) => excludedIds.includes(String(b._id)))}
        onChange={(_e, picked) =>
          setValue(
            `${base}.excludedBatches`,
            (picked as any[]).map((b) => String(b._id)),
          )
        }
        getOptionLabel={(b: any) => b?.name || 'Batch'}
        isOptionEqualToValue={(a: any, b: any) => String(a._id) === String(b._id)}
        disabled={!savedBatches.length}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Hide this quiz from these batches"
            helperText={
              savedBatches.length
                ? 'Everyone else still gets it. Students not in any batch are never affected.'
                : 'Add and save batches on this program first.'
            }
          />
        )}
        sx={{ mb: 2 }}
      />

      {fields.map((f, qi) => {
        const qBase = `${base}.questions.${qi}`;
        return (
          <Box key={f.id} sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <RHFTextField name={`${qBase}.title`} label={`Question ${qi + 1}`} size="small" fullWidth />
              <RHFTextField
                name={`${qBase}.title_ar`}
                label={`السؤال ${qi + 1}`}
                size="small"
                fullWidth
                inputProps={{ dir: 'rtl' }}
              />
              <RHFSwitch name={`${qBase}.multi`} label="Multi" />
              <IconButton color="error" size="small" onClick={() => remove(qi)}>
                <Iconify icon="eva:trash-2-outline" />
              </IconButton>
            </Stack>
            <AnswersField base={qBase} />
          </Box>
        );
      })}

      <Button
        variant="outlined"
        size="small"
        onClick={() => append({ title: '', title_ar: '', multi: false, type: 'mcq', answers: [] })}
        startIcon={<Iconify icon="eva:plus-fill" />}
      >
        Add question
      </Button>
      </Collapse>
    </Box>
  );
}
